import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, AlertTriangle, CheckCheck, Clock } from "lucide-react";
import { toast } from "sonner";

/** Envoi d'une note du répartiteur aux chauffeurs + accusés de lecture. */
export default function DispatchMessages() {
  const [open, setOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [sent, setSent] = useState([]);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("normal");
  const [picked, setPicked] = useState([]);   // vide = tous
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/dispatch/messages").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const send = async () => {
    if (!text.trim()) return toast.error("Écrivez le message");
    setBusy(true);
    try {
      const { data } = await api.post("/dispatch/messages", { text, priority, driver_ids: picked });
      toast.success(`Message envoyé à ${data.sent} chauffeur(s)`);
      setText(""); setPicked([]); setPriority("normal"); setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const unread = sent.filter((m) => !m.read_at).length;

  return (
    <div className="rounded-lg border border-border bg-card" data-testid="dispatch-messages">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <h3 className="font-display font-semibold flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-slate-400" /> Messages aux chauffeurs
        </h3>
        <div className="flex items-center gap-2">
          {unread > 0 && <Badge variant="secondary">{unread} non lu(s)</Badge>}
          <Button size="sm" onClick={() => setOpen(true)} data-testid="new-message-btn">
            <Send className="w-4 h-4 mr-1.5" /> Nouveau message
          </Button>
        </div>
      </div>
      <div className="divide-y divide-border max-h-[260px] overflow-y-auto">
        {!sent.length && <div className="px-5 py-8 text-sm text-muted-foreground text-center">Aucun message envoyé.</div>}
        {sent.slice(0, 40).map((m, i) => (
          <div key={m.id} className="px-5 py-2.5 text-sm flex items-start gap-3" data-testid={`sent-message-${i}`}>
            {m.priority === "urgent"
              ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              : <MessageSquare className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />}
            <div className="min-w-0 flex-1">
              <div className="truncate">{m.text}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {m.driver_name || "—"} · envoyé {(m.created_at || "").slice(11, 16)}
              </div>
            </div>
            {m.read_at
              ? <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1 shrink-0"><CheckCheck className="w-4 h-4" /> lu {m.read_at.slice(11, 16)}</span>
              : <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0"><Clock className="w-4 h-4" /> non lu</span>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Message aux chauffeurs</DialogTitle>
            <DialogDescription>Il s'affiche en grand dans l'app du chauffeur, qui doit toucher « Compris ».</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Ex. : Chemin Dutch fermé, passez par la 202." data-testid="message-text" />
            <div className="flex gap-2">
              {[["normal", "Normal"], ["urgent", "Urgent"]].map(([k, label]) => (
                <Button key={k} type="button" variant={priority === k ? "default" : "outline"} size="sm"
                  onClick={() => setPriority(k)} data-testid={`message-priority-${k}`}>{label}</Button>
              ))}
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">
                Destinataires {picked.length ? `(${picked.length} sélectionné(s))` : "— tous les chauffeurs"}
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {drivers.map((d) => {
                  const on = picked.includes(d.id);
                  return (
                    <button key={d.id} type="button" data-testid={`message-driver-${d.id}`}
                      onClick={() => setPicked(on ? picked.filter((x) => x !== d.id) : [...picked, d.id])}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                      {d.first_name} {d.last_name}
                    </button>
                  );
                })}
                {!drivers.length && <span className="text-xs text-muted-foreground">Aucun chauffeur enregistré.</span>}
              </div>
            </div>
            <Button className="w-full" onClick={send} disabled={busy} data-testid="message-send">
              <Send className="w-4 h-4 mr-1.5" /> Envoyer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
